/**
 * Cada deploy remove do servidor os chunks com hash antigo. Uma aba aberta
 * durante o deploy ainda referencia esses hashes: a próxima navegação lazy
 * falha com erro de import dinâmico até o usuário dar F5. Estes helpers
 * detectam esse caso e recarregam a página automaticamente (uma vez).
 */

const RELOAD_GUARD_KEY = 'ap-stale-chunk-reload-at'
const RELOAD_GUARD_WINDOW_MS = 30_000

const CHUNK_ERROR_PATTERNS = [
  /failed to fetch dynamically imported module/i, // Chrome/Edge
  /error loading dynamically imported module/i, // Firefox
  /importing a module script failed/i, // Safari
  /unable to preload css/i, // helper de preload do Vite
]

export function isChunkLoadError(error: unknown): boolean {
  return error instanceof Error && CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(error.message))
}

/**
 * Recarrega a página para buscar o index.html novo. Retorna false sem
 * recarregar se já houve reload há menos de 30s — se o erro persistir após
 * um reload, não é chunk de deploy antigo e deve seguir para o ErrorBoundary.
 */
export function reloadForStaleChunk(): boolean {
  let lastReloadAt: number
  try {
    lastReloadAt = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) ?? 0)
    if (Date.now() - lastReloadAt < RELOAD_GUARD_WINDOW_MS) {
      return false
    }
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()))
  } catch {
    // Sem sessionStorage não há como impedir loop de reload; deixar o
    // ErrorBoundary exibir o botão de recarregar.
    return false
  }
  window.location.reload()
  return true
}

/**
 * Verdadeiro quando `reloadForStaleChunk` já disparou um reload que ainda não
 * chegou — `window.location.reload()` é assíncrono, então o React continua
 * renderizando e ainda vai lançar o erro do chunk. Nesse intervalo o erro é
 * esperado e não deve virar tela de erro nem evento no Sentry.
 *
 * Distingue os dois motivos pelos quais `reloadForStaleChunk` retorna false:
 * reload em voo (aqui é true, engolir) x falha que persiste depois de um
 * reload (aqui é false, reportar).
 */
export function isReloadInFlight(): boolean {
  try {
    const lastReloadAt = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) ?? 0)
    return lastReloadAt > 0 && Date.now() - lastReloadAt < RELOAD_GUARD_WINDOW_MS
  } catch {
    // Mesma postura conservadora de `reloadForStaleChunk`: sem sessionStorage
    // não há como afirmar que existe reload em voo, então o erro é reportado.
    return false
  }
}

let recoveryInstalled = false

/**
 * Registra a recuperação de chunk velho para imports dinâmicos que falham
 * FORA de um render do React (ex.: `await import()` no AuthContext), onde não
 * há ErrorBoundary para capturar.
 *
 * Nunca chama `event.preventDefault()`. O `__vitePreload` do Vite termina em
 * `baseModule().catch(handlePreloadError)`, e o `handlePreloadError` só
 * relança se o evento NÃO foi cancelado. Cancelar transforma o catch em
 * retorno `undefined`: a promise do `import()` passa a *resolver* com
 * `undefined`, e o `lazyInitializer` do React executa `undefined.default` —
 * um `TypeError: Cannot read properties of undefined (reading 'default')` que
 * o `isChunkLoadError` não reconhece. Deixando o evento seguir, a rejeição
 * chega ao ErrorBoundary como erro de chunk de verdade.
 */
export function installStaleChunkRecovery(): void {
  if (recoveryInstalled) return
  recoveryInstalled = true

  window.addEventListener('vite:preloadError', () => {
    reloadForStaleChunk()
  })
}
